import { useQueryClient } from '@tanstack/react-query';
import { useMutation } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import { AclosetMessageError, isAclosetError } from '@looko-corp/acloset-react-kit';

import type { DomainPatchShopRequest, DomainShopResponse } from '@/Swagger';
import { aimsApi } from '@/axios/AclosetApi';
import { acCaptureException } from '@/thirdparty/ASentry/Log';

import { 매니저전용_쿼리키 } from '../manager/hooks/manager-query-keys';
import { queryKeys } from '../queryKeys';

type PatchRequestType = {
  shopID?: string;

  body: DomainPatchShopRequest;
};

/**
 * shop 업데이트
 */
export default function usePatchShop(
  showErrorSnackbar?: boolean,
  showSuccessSnackbar?: boolean,
): ReturnType<typeof usePatchShopBase>;

/**
 * shop 업데이트
 *
 * @deprecated shopID 없이 사용해주세요.
 */
export default function usePatchShop(shopID: string, showErrorSnackbar?: boolean): ReturnType<typeof usePatchShopBase>;

/**
 * shop 업데이트
 */
export default function usePatchShop(arg1?: string | boolean, arg2?: boolean): ReturnType<typeof usePatchShopBase> {
  return usePatchShopBase(
    typeof arg1 === 'boolean' ? undefined : arg1,
    typeof arg1 === 'boolean' ? arg1 : arg2,
    typeof arg1 === 'boolean' && typeof arg2 === 'boolean' ? arg2 : undefined,
  );
}

function usePatchShopBase(shopID?: string, showErrorSnackbar = true, showSuccessSnackbar?: boolean) {
  const { enqueueSnackbar } = useSnackbar();

  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: patchShop,
    onMutate: requestValue => {
      if (!requestValue.shopID) {
        return;
      }

      const prevShopInfo = queryClient.getQueryState<DomainShopResponse>(queryKeys.shopByID(requestValue.shopID))?.data;
      if (!prevShopInfo) {
        return;
      }

      queryClient.setQueryData(queryKeys.shopByID(requestValue.shopID), {
        ...prevShopInfo,
        ...requestValue.body,
      });

      return prevShopInfo;
    },
    onSuccess: (data, variables) => {
      if (!variables.shopID) {
        return;
      }
      if (showSuccessSnackbar) {
        enqueueSnackbar('설정이 저장되었습니다.', { variant: 'success' });
      }

      queryClient.invalidateQueries({ queryKey: 매니저전용_쿼리키.샵목록 });
      queryClient.invalidateQueries({ queryKey: queryKeys.shopByID(variables.shopID) });
    },
    onError: (err, requestValue, prevShopInfo) => {
      if (showErrorSnackbar) {
        acCaptureException('shop 업데이트 실패', err);
        if (isAclosetError(err)) {
          enqueueSnackbar(`샵 정보 업데이트에 실패했습니다 : ${err.userMessage}`, { variant: 'error' });
        } else {
          enqueueSnackbar('샵 정보 업데이트에 실패했습니다.', { variant: 'error' });
        }
      }

      if (!requestValue.shopID || !prevShopInfo) {
        return;
      }

      queryClient.setQueryData(queryKeys.shopByID(requestValue.shopID), prevShopInfo);
    },
  });
}

async function patchShop(data: PatchRequestType) {
  if (!data.shopID) {
    throw new AclosetMessageError('Empty shop ID');
  }
  return aimsApi.shops.shopsPartialUpdate(data.shopID, data.body);
}
